import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'

export const MediaDriverApiPath = {
  GET_MEDIA_DRIVER_DASHBOARD: '/api/sites/{site_id}/media-driver/dashboard', // GET Get Media Driver Dashboard
  DOWNLOAD_REWRITES_CSV: '/api/sites/{site_id}/media-driver/rewrites.csv' // GET Download Rewrites Csv
}

export type MediaDriverApiPath = (typeof MediaDriverApiPath)[keyof typeof MediaDriverApiPath]

export interface GetMediaDriverDashboardVars {
  site_id: number
}

export interface DownloadRewritesCsvVars {
  site_id: number
  days?: number
}

export interface MediaDriverSignal {
  headline: string
  has_data: boolean
  last_updated: string | null
}

// 分析の実施状況（実施済みか・直近30日の実行回数・遷移先ページ）
export interface AnalysisExecStatus {
  key: string
  label: string
  page: string
  executed: boolean
  recent_30d_count: number
  last_executed_at: string | null
}

export interface MarketTopKeyword {
  keyword: string
  monthly_volume: number | null
  analysis_count: number
}

export interface MarketSignal extends MediaDriverSignal {
  top_keywords: MarketTopKeyword[]
}

export interface CompetitorSignal extends MediaDriverSignal {
  competitor_count: number
  competitor_sites: string[]
  total_gaps: number
  analyses_status: AnalysisExecStatus[]
  executed_count: number
  total_count: number
}

// 自社内分析（コンテンツギャップ / カニバリ / 内部リンク）の実施状況
export interface SelfAnalysisStatus extends AnalysisExecStatus {
  purpose: string
  icon: string
  metric_text: string | null
}

export interface CompanySignal extends MediaDriverSignal {
  total_article_versions: number
  total_analyses: number
  // 直近4週間の週別リライト件数（3週前→今週）
  weekly_rewrite_trend: number[]
  avg_weekly_rewrites: number
  last_rewrite_at: string | null
  self_analyses: SelfAnalysisStatus[]
}

// サイト調査の実施状況（競合分析AI / SERP実データ / コンテンツギャップ分析）
export interface SiteSurveySubStatus {
  key: MediaAnalysisType
  tab: string | null
  page: string
  label: string
  purpose: string
  executed: boolean
  recent_30d_count: number
  last_executed_at: string | null
  summary: string | null
  metric_text: string | null
}

export interface SiteSurveySignal {
  headline: string
  executed_count: number
  total_count: number
  all_executed: boolean
  subs: SiteSurveySubStatus[]
}

// 今週の更新詳細
export interface WeeklyRewriteUpdate {
  id: number
  page_id: string
  title: string | null
  version_number: number
  created_at: string | null
}

export interface WeeklyArticleUpdate {
  id: number
  type: string
  title: string | null
  created_at: string | null
}

export interface WeeklyAnalysisUpdate {
  id: number
  analysis_type: MediaAnalysisType
  summary: string | null
  created_at: string | null
}

export interface MediaWeeklyReport {
  summary: {
    rewrite_count: number
    article_count: number
    analysis_count: number
    missing_article_count: number
    text: string
  }
  updates: {
    rewrites: WeeklyRewriteUpdate[]
    articles: WeeklyArticleUpdate[]
    analyses: WeeklyAnalysisUpdate[]
  }
  next_actions: { priority: number; action: string; reason: string; link: string }[]
}

// 前回比較（トレンドKW変動・広告環境の変化）
export interface AdChange {
  keyword: string
  cpc_change: number
  current_cpc: number | null
  previous_cpc: number | null
  channel_changed: boolean
  previous_channel: string | null
  current_channel: string | null
}

export interface PreviousComparisonUnavailable {
  available: false
  message: string
}

export interface PreviousComparisonAvailable {
  available: true
  current: { id: number; created_at: string | null }
  previous: { id: number; created_at: string | null }
  new_trends: string[]
  dropped_trends: string[]
  ad_changes: AdChange[]
}

export type PreviousComparison = PreviousComparisonUnavailable | PreviousComparisonAvailable

export interface GetMediaDriverDashboardResponse {
  site_id: number
  site_name: string
  generated_at: string
  week_start: string
  current_status: {
    market: MarketSignal
    competitor: CompetitorSignal
    company: CompanySignal
    site_survey: SiteSurveySignal
  }
  previous_comparison: PreviousComparison
  weekly_report: MediaWeeklyReport
}

export type DownloadRewritesCsvResponse = Blob
